import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import * as React from 'react';
import MuiDialogHeader from '../Shared/MuiDialogHeader';
import { useTranslation } from 'react-i18next';
import { SavePricingDatabaseSharingCommand, SharedPricingDatabaseDTO, UsersGateApiClient } from '../Api/ApiServerHooks';
import { useGateApi } from '../Api/useGateApi';
import GiffLoader from '../Shared/GiffLoader';

export interface IPricingDatabaseSharingDialogProps {
    isOpen: boolean;
    onClose: (refresh: boolean) => void;
    tenantId: number;
    tenantName?: string;
}

/// Lets an admin pick which other tenants' pricing databases this tenant may read. The share is a
/// link, not a copy: the owning tenant keeps the database and stays the only side able to edit it.
export default function PricingDatabaseSharingDialog(props: Readonly<IPricingDatabaseSharingDialogProps>) {
    const { isOpen, onClose, tenantId, tenantName } = props;
    const { t } = useTranslation();
    const { authorizedApi } = useGateApi(UsersGateApiClient);

    const [databases, setDatabases] = React.useState<SharedPricingDatabaseDTO[]>();
    const [filter, setFilter] = React.useState('');
    const [saving, setSaving] = React.useState(false);

    const load = React.useCallback(async () => {
        if (!isOpen || tenantId <= 0) return;
        setDatabases(undefined);
        const response = await authorizedApi?.getPricingDatabaseSharing(tenantId);
        if (response?.pricingDatabases)
            setDatabases(response.pricingDatabases);
    }, [authorizedApi, tenantId, isOpen]);

    React.useEffect(() => {
        load();
    }, [load]);

    // Grouped by owning tenant so it is always obvious whose data is being handed over.
    const groups = React.useMemo(() => {
        const needle = filter.trim().toLowerCase();
        const matching = (databases ?? []).filter(db => needle.length === 0
            || (db.name ?? '').toLowerCase().includes(needle)
            || (db.ownerTenantName ?? '').toLowerCase().includes(needle));

        const byOwner = new Map<number, { ownerName: string; databases: SharedPricingDatabaseDTO[] }>();
        for (const db of matching) {
            const key = db.ownerTenantId ?? 0;
            if (!byOwner.has(key))
                byOwner.set(key, { ownerName: db.ownerTenantName ?? '', databases: [] });
            byOwner.get(key)!.databases.push(db);
        }
        return Array.from(byOwner.values());
    }, [databases, filter]);

    const toggle = React.useCallback((id?: number, shared?: boolean) => {
        setDatabases(prev => prev?.map(db => db.id === id
            ? new SharedPricingDatabaseDTO({ ...db, shared: shared })
            : db));
    }, []);

    const save = React.useCallback(async () => {
        if (!databases) return;
        setSaving(true);
        try {
            const response = await authorizedApi?.savePricingDatabaseSharing(new SavePricingDatabaseSharingCommand({
                tenantId: tenantId,
                pricingDatabases: databases
            }));
            if (response?.success)
                onClose(true);
        } finally {
            setSaving(false);
        }
    }, [authorizedApi, databases, tenantId, onClose]);

    return (
        <Dialog fullWidth maxWidth='sm' onClose={() => onClose(false)} open={isOpen} sx={{ zIndex: 1299 }}>
            <MuiDialogHeader
                showCloseText
                title={tenantName
                    ? `${t('Administration.pricingDatabaseSharing')} — ${tenantName}`
                    : t('Administration.pricingDatabaseSharing')}
                onClose={() => onClose(false)} />

            <DialogContent>
                {!databases && <GiffLoader />}
                {databases && (
                    <Stack spacing={2} sx={{ padding: '8px 0' }}>
                        <Typography variant='body2' color='text.secondary'>
                            {t('Administration.pricingDatabaseSharingHint')}
                        </Typography>
                        <TextField
                            size='small'
                            label={t('Search')}
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                        />
                        {groups.length === 0 && (
                            <Typography variant='body2' color='text.secondary'>
                                {t('Administration.noPricingDatabasesToShare')}
                            </Typography>
                        )}
                        {groups.map(group => (
                            <Box key={group.ownerName}>
                                <Typography variant='subtitle2'>{group.ownerName}</Typography>
                                <Box sx={{ pl: 1, display: 'flex', flexDirection: 'column' }}>
                                    {group.databases.map(db => (
                                        <FormControlLabel
                                            key={db.id}
                                            label={db.name}
                                            control={
                                                <Checkbox
                                                    checked={db.shared ?? false}
                                                    onChange={(_, checked) => toggle(db.id, checked)}
                                                />
                                            }
                                        />
                                    ))}
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Stack direction='row' spacing={2} sx={{ padding: '8px 16px' }}>
                    <Button variant='outlined' color='primary' onClick={() => onClose(false)}>{t('Close')}</Button>
                    <Button disabled={!databases || saving} variant='contained' color='primary' onClick={save}>
                        {t('Save')}
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
}
